import React from 'react';
import { PropTypes } from 'prop-types';
import Number from './Number';
import { generateComponentDocs, generateStoryDocs } from '../../../utils/documentationUtils';

export default {
  title: 'Components/Inputs/Number',
  component: Number,
  parameters: {
    layout: 'centered',
    ...generateComponentDocs(
      // What problem does it solve?
      '\nThe `Number` component provides a simple, styled number input field for forms. It supports validation, custom styles, and optional labels, making it easy to integrate into various form layouts.\n\n',
      // When & how to use it?
      '\nUse the `Number` component when a numeric input field is required in a form. It is designed for both inline and standalone use cases, supporting customization options for labels, spacing, and borders.\n\n',
      // Current Usage
      `\nThe \`Number\` component is commonly used in forms across the application for input fields such as:
      \n- Age
      \n- Quantity
      \n- Numeric Filters\n\n`,
      // Related Components
      `\n- **[TextInput](http://localhost:6006/?path=/docs/components-inputs-textinput--docs)**: Similar component used for text inputs.
       \n- **[CustomSelect](http://localhost:6006/?path=/docs/components-inputs-customselect--docs)**: Used for dropdown inputs in forms.\n\n`,
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    label: {
      control: { type: 'text' },
      description: 'The label displayed alongside the input field.\n\n',
      table: {
        type: { summary: 'string' },
        required: false,
      },
    },
    name: {
      control: { type: 'text' },
      description: 'The name attribute for the input field, used for form submissions.\n\n',
      table: {
        type: { summary: 'string' },
        required: true,
      },
    },
    value: {
      control: { type: 'number' },
      description: 'The current value of the input field.\n\n',
      table: {
        type: { summary: 'string | number' },
        required: false,
      },
    },
    setValue: {
      control: { type: 'function' },
      description: 'Callback function to update the value of the input field.\n\n',
      table: {
        type: { summary: 'function' },
        required: true,
      },
    },
    placeholder: {
      control: { type: 'text' },
      description: 'The placeholder text displayed when the input field is empty.\n\n',
      table: {
        type: { summary: 'string' },
        required: false,
      },
    },
    isValid: {
      control: { type: 'boolean' },
      description: 'Determines whether the input field is valid (affects styling).\n\n',
      table: {
        type: { summary: 'boolean' },
        defaultValue: { summary: 'true' },
        required: false,
      },
    },
    className: {
      control: { type: 'text' },
      description: 'Additional classes for custom styling.\n\n',
      table: {
        type: { summary: 'string' },
        required: false,
      },
    },
    borderColor: {
      control: { type: 'text' },
      description: 'Color of the input field border. Defaults to "border-vamos-blue-gray".\n\n',
      table: {
        type: { summary: 'string' },
        defaultValue: { summary: 'border-vamos-blue-gray' },
        required: false,
      },
    },
    isLabelBeside: {
      control: { type: 'boolean' },
      description: 'Positions the label beside the input field instead of above it.\n\n',
      table: {
        type: { summary: 'boolean' },
        defaultValue: { summary: 'false' },
        required: false,
      },
    },
    fontSize: {
      control: { type: 'text' },
      description: 'Font size for the input text. Defaults to "text-base".\n\n',
      table: {
        type: { summary: 'string' },
        required: false,
      },
    },
    spaceY: {
      control: { type: 'text' },
      description: 'Vertical spacing between elements. Defaults to "space-y-2".\n\n',
      table: {
        type: { summary: 'string' },
        required: false,
      },
    },
  },
};

const NumberTemplate = ({
  label, data,
}) => {
  const [value, setValue] = React.useState(data);
  return (
    <Number
      label={label}
      value={value}
      setValue={setValue}
    />
  );
};

const Template = NumberTemplate.bind({});

export const Default = Template.bind({});
Default.args = {
  label: 'Number Input',
};

Default.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant is used as the default configuration for a number input field. It can be styled and customized based on requirements.\n\n',
  // Where is it currently used on the product?
  '\nThe `Number` component is widely used in forms across various pages for collecting numeric input.\n\n',
);

export const SavedValue = Template.bind({});
SavedValue.args = {
  label: 'Age',
  data: 28,
};

SavedValue.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant is used to display a number input field pre-filled with a saved value.\n\n',
  // Where is it currently used on the product?
  '\nThe `Number` component in this variant is used in forms where numeric values may be pre-set or edited.\n\n',
);

NumberTemplate.propTypes = {
  label: PropTypes.string,
  data: PropTypes.number,
};
